import type { CaptureResult } from 'posthog-js'

const REDACTED = '<redacted>'

const SENSITIVE_PARAM_NAMES = new Set([
  'access_token',
  'refresh_token',
  'id_token',
  'provider_token',
  'provider_refresh_token',
  'token',
  'code',
])

// The phone capture route (`/<locale>/capture`) carries the live capture
// session token in the URL hash (`#s=<session>&t=<token>&h=<holdout>`) under
// deliberately short keys that are NOT in SENSITIVE_PARAM_NAMES. Adding `t`/`s`
// to that set would over-redact unrelated URLs, so instead we drop the whole
// fragment whenever the pathname is the capture route.
const CAPTURE_ROUTE_PATTERN = /\/capture\/?$/

const URL_PROPERTY_KEYS = new Set([
  '$current_url',
  '$referrer',
  // PostHog stamps the session's first URL onto every event in the session via
  // these autocaptured properties. On the capture route that first URL carries
  // the token-bearing hash, so they leak it exactly like `$current_url` unless
  // they go through the same sanitizer.
  '$session_entry_url',
  '$session_entry_referrer',
  'current_url',
  'url',
  'href',
  'referrer',
  'redirect_url',
  'redirect_to',
  'return_url',
])

const sensitiveParamPattern = Array.from(SENSITIVE_PARAM_NAMES).join('|')
const sensitiveParamRegex = new RegExp(`([?&#](${sensitiveParamPattern})=)[^&#]+`, 'gi')

function hashContainsSensitiveParams(hash: string): boolean {
  if (!hash) return false
  const hashParams = hash.startsWith('#') ? hash.slice(1) : hash
  if (!hashParams) return false
  return Array.from(new URLSearchParams(hashParams).keys()).some(key => SENSITIVE_PARAM_NAMES.has(key.toLowerCase()))
}

export function sanitizeTelemetryUrl(value: string): string {
  if (!value) return value

  try {
    const base =
      typeof window !== 'undefined' && window.location?.origin ? window.location.origin : 'https://martini.local'
    const url = new URL(value, base)
    for (const key of Array.from(url.searchParams.keys())) {
      if (SENSITIVE_PARAM_NAMES.has(key.toLowerCase())) url.searchParams.set(key, REDACTED)
    }
    if (url.hash && (CAPTURE_ROUTE_PATTERN.test(url.pathname) || hashContainsSensitiveParams(url.hash))) {
      url.hash = '#redacted'
    }
    // Keep genuinely path-relative inputs ('/foo') relative, but NOT
    // protocol-relative ones ('//host/foo') — the relative form would silently
    // drop the host and corrupt the recorded URL. Let those fall through to the
    // absolute serialization, which preserves the host.
    const isPathRelative = value.startsWith('/') && !value.startsWith('//')
    return isPathRelative ? `${url.pathname}${url.search}${url.hash}` : url.toString()
  } catch {
    return value.replace(sensitiveParamRegex, `$1${REDACTED}`)
  }
}

function sanitizePropertyMap(properties: Record<string, unknown> | undefined): Record<string, unknown> | undefined {
  if (!properties) return properties
  let sanitized: Record<string, unknown> | null = null

  for (const [key, propValue] of Object.entries(properties)) {
    if (typeof propValue !== 'string' || !URL_PROPERTY_KEYS.has(key)) continue
    const nextValue = sanitizeTelemetryUrl(propValue)
    if (nextValue === propValue) continue
    if (!sanitized) sanitized = { ...properties }
    sanitized[key] = nextValue
  }

  return sanitized ?? properties
}

export function sanitizePostHogCaptureResult(result: CaptureResult | null): CaptureResult | null {
  if (!result) return result
  const properties = sanitizePropertyMap(result.properties)
  const setProperties = sanitizePropertyMap(result.$set)
  const setOnceProperties = sanitizePropertyMap(result.$set_once)
  if (properties === result.properties && setProperties === result.$set && setOnceProperties === result.$set_once) {
    return result
  }
  return {
    ...result,
    properties: properties ?? result.properties,
    $set: setProperties,
    $set_once: setOnceProperties,
  }
}

export interface SentryLikeEvent {
  request?: {
    url?: string
    headers?: Record<string, string | undefined>
  }
  extra?: Record<string, unknown>
  tags?: Record<string, unknown>
  contexts?: Record<string, unknown>
}

export function sanitizeSentryEvent<T extends SentryLikeEvent>(event: T): T {
  const requestUrl = event.request?.url
  const sanitizedRequestUrl = typeof requestUrl === 'string' ? sanitizeTelemetryUrl(requestUrl) : requestUrl
  const extra = sanitizePropertyMap(event.extra)

  if (sanitizedRequestUrl === requestUrl && extra === event.extra) return event

  return {
    ...event,
    request: event.request
      ? {
          ...event.request,
          url: sanitizedRequestUrl,
        }
      : event.request,
    extra,
  }
}
